import { motion, AnimatePresence } from "framer-motion";
import {
  Rocket, Target, Sparkles, CheckCircle2, ListChecks, Trophy, Flame,
  Repeat, Sun, CalendarCheck, Award, MessageSquare, Users, BookOpen, Lock, X, PartyPopper,
} from "lucide-react";
import { ShareWinButton } from "@/components/app/ShareWin";

export const BADGE_ICONS = {
  Rocket, Target, Sparkles, CheckCircle2, ListChecks, Trophy, Flame,
  Repeat, Sun, CalendarCheck, Award, MessageSquare, Users, BookOpen,
};

const TIER = {
  bronze: { ring: "#E07A5F", bg: "#FCE9E2", text: "#C2410C", label: "Bronze" },
  silver: { ring: "#8FA3B8", bg: "#EEF2F6", text: "#5A6B7B", label: "Silver" },
  gold: { ring: "#FFB347", bg: "#FFF4E6", text: "#B45309", label: "Gold" },
};

export function Badge({ a, showShare = false }) {
  const Icon = BADGE_ICONS[a.icon] || Award;
  const t = TIER[a.tier] || TIER.bronze;
  return (
    <div data-testid={`badge-${a.id}`}
      className={`group relative flex flex-col items-center rounded-2xl border p-4 text-center transition-transform duration-200 hover:-translate-y-1 ${a.unlocked ? "border-[#FFE8E3] bg-white shadow-warm" : "border-dashed border-[#E2E8F0] bg-[#FFF9F5]"}`}>
      <div className="flex h-14 w-14 items-center justify-center rounded-full"
        style={{ background: a.unlocked ? t.bg : "#EDF0F3", border: `2px solid ${a.unlocked ? t.ring : "#E2E8F0"}` }}>
        {a.unlocked
          ? <Icon className="h-6 w-6" strokeWidth={2.5} style={{ color: t.text }} />
          : <Lock className="h-5 w-5 text-[#A0AEC0]" strokeWidth={2.5} />}
      </div>
      <p className={`mt-2 font-nunito text-sm font-bold ${a.unlocked ? "text-[#2D3748]" : "text-[#A0AEC0]"}`}>{a.title}</p>
      <p className="mt-0.5 font-quicksand text-[11px] leading-tight text-[#718096]">{a.desc}</p>
      {a.unlocked && (
        <span className="mt-2 rounded-full px-2 py-0.5 font-nunito text-[10px] font-bold" style={{ background: t.bg, color: t.text }}>{t.label}</span>
      )}
      {a.unlocked && showShare && (
        <div className="mt-3">
          <ShareWinButton testid={`share-badge-${a.id}`} label="Share"
            payload={{ kind: "badge", title: a.title, subtitle: a.desc, stat: t.label.toUpperCase() + " BADGE" }} />
        </div>
      )}
    </div>
  );
}

export function UnlockModal({ badges, onClose }) {
  if (!badges || badges.length === 0) return null;
  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[80] flex items-center justify-center bg-[#2D3748]/50 p-4 backdrop-blur-sm" onClick={onClose}>
        <motion.div initial={{ scale: 0.85, y: 24 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.85, opacity: 0 }}
          transition={{ type: "spring", stiffness: 280, damping: 22 }}
          className="relative w-full max-w-sm overflow-hidden rounded-[2rem] bg-white p-8 text-center shadow-warm-lg"
          onClick={(e) => e.stopPropagation()} data-testid="unlock-modal">
          <button onClick={onClose} data-testid="close-unlock-btn" className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-[#FFF9F5] text-[#718096] hover:bg-[#FFE8E3]"><X className="h-4 w-4" /></button>
          {/* confetti */}
          <div className="pointer-events-none absolute inset-x-0 top-0 flex justify-center">
            {Array.from({ length: 14 }).map((_, i) => {
              const colors = ["#FF7E67", "#FFB347", "#FFD166", "#06D6A0", "#E07A5F"];
              return (
                <motion.span key={i} className="absolute h-2 w-2 rounded-full" style={{ background: colors[i % colors.length] }}
                  initial={{ x: 0, y: -10, opacity: 1 }}
                  animate={{ x: (i - 7) * 26, y: 160 + (i % 3) * 30, opacity: 0, rotate: 220 }}
                  transition={{ duration: 1.1, delay: i * 0.03, ease: "easeOut" }} />
              );
            })}
          </div>
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#FFF4E6]">
            <PartyPopper className="h-6 w-6 text-[#FFB347]" strokeWidth={2.5} />
          </div>
          <h3 className="mt-4 font-nunito text-2xl font-extrabold text-[#2D3748]">
            {badges.length > 1 ? `${badges.length} Badges Unlocked!` : "Badge Unlocked!"}
          </h3>
          <div className="mt-5 space-y-3">
            {badges.map((b) => <Badge key={b.id} a={{ ...b, unlocked: true }} />)}
          </div>
          {badges.length === 1 && (
            <div className="mt-4 flex justify-center">
              <ShareWinButton testid="unlock-share-btn"
                payload={{ kind: "badge", title: badges[0].title, subtitle: badges[0].desc,
                           stat: (badges[0].tier || "bronze").toUpperCase() + " BADGE" }} />
            </div>
          )}
          <button onClick={onClose} className="mt-6 w-full rounded-full bg-sunrise py-3.5 font-nunito font-bold text-white shadow-glow-coral transition-transform duration-200 hover:scale-[1.02]">
            Keep Going
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
